import { formatISO } from 'date-fns';
import { useState } from 'react';
import { gql, useClient } from 'urql';

import { RangeDate } from '@/globalTypes';
import { useQueryParamsWithTime } from '@/hooks';
import { downloadFile, mapVariables } from '@/utils';
import {
  GlobalPlayerInformationHistoryReportQuery,
  GlobalPlayerInformationHistoryReportQueryVariables,
} from './__generated__/useGlobalPlayerInformationHistoryReport';
import { queryParams } from './queryParams';

const GLOBAL_PLAYER_INFORMATION_HISTORY_REPORT_QUERY = gql`
  query GlobalPlayerInformationHistoryReport(
    $playerGlobalId: ID
    $playerIds: [ID!]!
    $playerGlobalUUID: String
    $playerUUIDs: [String!]!
    $eventType: PlayerInformationHistoryEventType
    $timestamp: RangeDate
    $service: Service
    $orderBy: PlayerInformationHistoryOrderBy
    $desc: Boolean
  ) {
    viewer {
      id
      globalPlayerInformationHistoryExport: playerInformationHistoryExport(
        playerGlobalId: $playerGlobalId
        playerIds: $playerIds
        playerGlobalUUID: $playerGlobalUUID
        playerUUIDs: $playerUUIDs
        eventType: $eventType
        timestamp: $timestamp
        service: $service
        orderBy: $orderBy
        desc: $desc
      )
    }
  }
`;

export default function useGlobalPlayerInformationHistoryReport(
  playerGlobalId: string,
) {
  const [query] = useQueryParamsWithTime(queryParams, {
    fromAndToOptional: true,
  });
  const [loading, setLoading] = useState(false);
  const client = useClient();

  const generate = async () => {
    if (loading) {
      return;
    }

    setLoading(true);

    const timestamp: RangeDate | undefined =
      query.from && query.to
        ? {
            from: formatISO(query.from),
            to: formatISO(query.to),
          }
        : undefined;

    const playerUUIDs = query.playerUUID ? [query.playerUUID] : [];

    const variables: GlobalPlayerInformationHistoryReportQueryVariables = {
      ...mapVariables(query),
      playerGlobalId,
      playerIds: [],
      playerUUIDs,
      timestamp,
    };

    try {
      const res = await client
        .query<
          GlobalPlayerInformationHistoryReportQuery,
          GlobalPlayerInformationHistoryReportQueryVariables
        >(GLOBAL_PLAYER_INFORMATION_HISTORY_REPORT_QUERY, variables)
        .toPromise();

      const url = res.data?.viewer.globalPlayerInformationHistoryExport;

      if (url) {
        await downloadFile(url, {
          fileName: 'global-player-information-history-report.csv',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    generate,
    loading,
  };
}
